/**
 * DatabaseConnectionTable Component
 *
 * Displays a table of database connections with actions (view, edit, delete, grant access).
 */

import React from 'react';
import { ServerStackIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { Badge } from '../../../components/ui/Badge';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { DatabaseConnection } from '../../../types/database-connection.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface DatabaseConnectionTableProps {
  databaseConnections: DatabaseConnection[];
  isLoading?: boolean;
  onView: (dbConnection: DatabaseConnection) => void;
  onEdit?: (dbConnection: DatabaseConnection) => void;
  onDelete?: (dbConnection: DatabaseConnection) => void;
  /** Permission to edit database connections */
  canEdit?: boolean;
  /** Permission to delete database connections */
  canDelete?: boolean;
}

/**
 * Get badge variant for database engine
 */
const getEngineVariant = (engine: string): 'info' | 'success' | 'warning' | 'error' => {
  switch (engine) {
    case 'SQL_SERVER':
      return 'info';
    case 'MYSQL':
      return 'success';
    case 'POSTGRESQL':
      return 'warning';
    case 'ORACLE':
      return 'error';
    default:
      return 'info';
  }
};

/**
 * Get display name for database engine
 */
const getEngineDisplayName = (engine: string): string => {
  switch (engine) {
    case 'SQL_SERVER':
      return 'SQL Server';
    case 'MYSQL':
      return 'MySQL';
    case 'POSTGRESQL':
      return 'PostgreSQL';
    case 'ORACLE':
      return 'Oracle';
    default:
      return engine;
  }
};

/**
 * DatabaseConnectionTable component
 */
export const DatabaseConnectionTable: React.FC<DatabaseConnectionTableProps> = ({
  databaseConnections,
  isLoading,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  if (databaseConnections.length === 0) {
    return (
      <EmptyState
        icon={<ServerStackIcon className="mx-auto h-12 w-12" />}
        title="No database connections found"
        description="Get started by creating a new database connection."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Connection Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Engine
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Host
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {databaseConnections.map((dbConnection) => (
            <tr key={dbConnection.dbConnectionId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <ServerStackIcon className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {dbConnection.dbConnectionName}
                    </div>
                    {dbConnection.description && (
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">
                        {dbConnection.description}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CopyableCode value={dbConnection.dbConnectionCode} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <Badge variant={getEngineVariant(dbConnection.engine)}>
                    {getEngineDisplayName(dbConnection.engine)}
                  </Badge>
                  {dbConnection.connectionType === 'KETRICS_MANAGED' && (
                    <Badge variant="info">Managed</Badge>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {dbConnection.connectionType === 'KETRICS_MANAGED' ? (
                  <div className="text-sm text-gray-500 italic">Ketrics-Managed</div>
                ) : (
                  <>
                    <div className="text-sm text-gray-900">{dbConnection.host}</div>
                    <div className="text-xs text-gray-500">:{dbConnection.port}</div>
                  </>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge variant={dbConnection.isActive ? 'success' : 'warning'}>
                  {dbConnection.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatRelativeTime(dbConnection.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(dbConnection)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  {canEdit && onEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(dbConnection)}
                      title="Edit Connection"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && onDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(dbConnection)}
                      title="Delete Connection"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
